import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AdreseCentreService } from '../adrese-centre.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { AdreseCentreAddPayload, AdreseCentreEditPayload } from '../adrese-centre.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';


@Component({
    selector: 'app-edit-adrese-centre',
    templateUrl: './edit-adrese-centre.component.html',
    styleUrls: ['./edit-adrese-centre.component.scss']
})
export class EditAdreseCentreComponent {
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;

    isLoading = false;

    form: FormGroup = new FormGroup({
        type: new FormControl(this.data.type, []),
        web_name: new FormControl(this.data.web_name, [Validators.required]),

        tara_id: new FormControl(this.data.tara_id || -1, [Validators.required]),
        tara_nume: new FormControl(this.data.tara_nume, []),
        judet_id: new FormControl(this.data.judet_id || -1, [Validators.required]),
        judet_nume: new FormControl(this.data.judet_nume, []),
        localitate_id: new FormControl(this.data.localitate_id || -1, [Validators.required]),
        localitate_nume: new FormControl(this.data.localitate_nume, []),
        sector: new FormControl(this.data.sector || '0', []),
        strada_id: new FormControl(this.data.strada_id || -1, [Validators.required]),
        strada_nume: new FormControl(this.data.strada_nume, []),
        numar: new FormControl(this.data.numar, [Validators.required]),
        bloc: new FormControl(this.data.bloc, []),
        etaj: new FormControl(this.data.etaj, []),
        scara: new FormControl(this.data.scara, []),
        apartament: new FormControl(this.data.apartament, []),
        cod_postal: new FormControl(this.data.cod_postal, []),
        observatii: new FormControl(this.data.observatii, []),
    });

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    private destroy$ = new Subject<void>();

    tari: Generic[] = [];
    filteredTari: any[] = [];
    tariControl = new FormControl();

    judete: Generic[] = [];
    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    localitati: Generic[] = [];
    localitatiControl = new FormControl();
    isLocalitatiLoading = false;
    showLocalitatiMessage = true;
    localitatiLoadComplete = false;

    strazi: Generic[] = [];
    straziControl = new FormControl();
    isStraziLoading = false;
    showStraziMessage = true;
    straziLoadComplete = false;


    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<EditAdreseCentreComponent>,
        private requestService: AdreseCentreService,
        private commonService: CommonService,
        private notificationService: NotificationService,
    ) {
        console.log(this.data);
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit() {
        this.loadTari();

        if (this.data.tara_id) {
            this.loadJudete(this.data.tara_id);
        }
        if (this.data.localitate_nume) {
            this.fetchLocalitati(this.data.localitate_nume);
        }
        if (this.data.strada_nume) {
            this.fetchStrazi(this.data.strada_nume);
        }

        this.form.get('tara_id')?.disable();

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.tariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.localitatiControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchLocalitati(localitate);
                this.showLocalitatiMessage = true;
                this.localitatiLoadComplete = false;
            });

        this.straziControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchStrazi(localitate);
                this.showStraziMessage = true;
                this.straziLoadComplete = false;
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadTari() {
        this.isLoading = true;
        this.commonService
            .getTariActive()
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tari = results;
                    this.filteredTari = [...this.tari];
                },
                error: (error) => {
                    console.error('Error loading tari', error);
                    this.filteredTari = [];
                },
            });
    }

    onTariSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.form.get('tara_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTariChange(taraId: number): void {
        this.form.controls['judet_id'].patchValue(-1);
        this.form.controls['localitate_id'].patchValue(-1);
        this.form.controls['strada_id'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];

            this.localitati = [];
            this.strazi = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);

            const selectedTara = this.tari.find(tara => tara.id === taraId);

            if (selectedTara) {
                this.form.get('tara_nume')?.patchValue(selectedTara.name);
            }
        }
    }

    loadJudete(taraId: number) {
        this.isLoading = true;
        this.commonService
            .getJudeteActiveSearch(taraId)
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (results) => {
                    this.judete = [...results];
                    this.filteredJudete = [...results];
                },
                error: (error) => {
                    console.log('Error loading judete', error);
                    this.filteredJudete = [];
                }
            });
    }

    onJudeteChange(judetId: number) {
        this.form.controls['localitate_id'].patchValue(-1);
        this.localitatiControl.patchValue('');
        this.localitati = [];
        this.showLocalitatiMessage = true;
        this.localitatiLoadComplete = false;

        this.form.controls['strada_id'].patchValue(-1);
        this.straziControl.patchValue('');
        this.strazi = [];
        this.showStraziMessage = true;
        this.straziLoadComplete = false;

        if (judetId) {
            const selectedJudet = this.judete.find(judet => judet.id === judetId);

            if (selectedJudet) {
                this.form.get('judet_nume')?.patchValue(selectedJudet.name);
            }
        }
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.form.get('judet_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }

    fetchLocalitati(localitate: string) {
        const taraValue = this.form.get('tara_id')?.value;
        const judetValue = this.form.get('judet_id')?.value;
        const tara = taraValue && taraValue !== -1 ? this.form.get('tara_id')?.value : undefined;
        const judet = judetValue && judetValue !== -1 ? this.form.get('judet_id')?.value : undefined;

        if (localitate && localitate.length >= 2) {
            this.isLocalitatiLoading = true;
            this.commonService.getLocalitatiActive(tara, judet, localitate).subscribe({
                next: (response: any) => {
                    this.localitati = response;
                    this.isLocalitatiLoading = false;
                    this.localitatiLoadComplete = true;
                    this.showLocalitatiMessage = this.localitati.length === 0;
                },
                error: (err: any) => {
                    this.isLocalitatiLoading = false;
                    this.showLocalitatiMessage = true;
                    this.localitatiLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.localitati = [];
            this.showLocalitatiMessage = true;
            this.localitatiLoadComplete = false;
        }
    }

    onLocalitatiChange(localitateId: number) {
        this.form.controls['strada_id'].patchValue(-1);
        this.straziControl.patchValue('');
        this.strazi = [];
        this.showStraziMessage = true;
        this.straziLoadComplete = false;

        if (localitateId) {
            const selectedLocalitate = this.localitati.find(localitate => localitate.id === localitateId);

            if (selectedLocalitate) {
                this.form.get('localitate_nume')?.patchValue(selectedLocalitate.name);
            }
        }
    }

    fetchStrazi(strada: string) {
        const taraValue = this.form.get('tara_id')?.value;
        const judetValue = this.form.get('judet_id')?.value;
        const localitateEmitentValue = this.form.get('localitate_id')?.value;
        const tara = taraValue && taraValue !== -1 ? this.form.get('tara_id')?.value : undefined;
        const judet = judetValue && judetValue !== -1 ? this.form.get('judet_id')?.value : undefined;
        const localitate = localitateEmitentValue && localitateEmitentValue !== -1 ? localitateEmitentValue : undefined;

        if (strada && strada.length >= 2) {
            this.isStraziLoading = true;
            this.commonService.getStraziActive(tara, judet, localitate, strada).subscribe({
                next: (response: any) => {
                    this.strazi = response;
                    this.isStraziLoading = false;
                    this.straziLoadComplete = true;
                    this.showStraziMessage = this.strazi.length === 0;
                },
                error: (err: any) => {
                    this.isStraziLoading = false;
                    this.showStraziMessage = true;
                    this.straziLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.strazi = [];
            this.showStraziMessage = true;
            this.straziLoadComplete = false;
        }
    }

    onStraziChange(stradaId: number) {
        if (stradaId) {
            const selectedStrada = this.strazi.find(strada => strada.id === stradaId);

            if (selectedStrada) {
                this.form.get('strada_nume')?.patchValue(selectedStrada.name);
            }
        }
    }

    submitForm() {
        this.isLoading = true;

        const payload: AdreseCentreEditPayload = {
            ...this.form.value,
            id: this.data.id,
            tara_id: 1
        }

        this.requestService
            .updateEntry(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
